import { fetchCurrentHoldingVT2 } from 'views/Portfolio/components/Details/services/fetchCurrentHoldings';
import { fetchCurrentAllocations } from 'views/Portfolio/components/Allocation/services/fetchCurrentAllocations';
import { ToolExecutionResult, CellarInsightData } from '../types';
import { formatCurrency, formatPercentage } from '../utils/formatters';

// Simplified maturity window logic (mirrors useCellarInsights.ts logic)
const getMaturityStatus = (
  vintage: string | null | undefined,
  region: string | null | undefined,
): 'approaching' | 'peak' | 'past' | 'unknown' => {
  if (!vintage) return 'unknown';

  const vintageNum = parseInt(vintage, 10);
  if (Number.isNaN(vintageNum)) return 'unknown';

  const currentYear = new Date().getFullYear();
  const wineAge = currentYear - vintageNum;

  // Simplified maturity windows by region
  const maturityWindows: Record<string, { peakStart: number; peakEnd: number }> = {
    Bordeaux: { peakStart: 10, peakEnd: 25 },
    Burgundy: { peakStart: 8, peakEnd: 20 },
    Champagne: { peakStart: 5, peakEnd: 15 },
    'Rhone Valley': { peakStart: 8, peakEnd: 20 },
    Italy: { peakStart: 8, peakEnd: 20 },
    'Rest of World': { peakStart: 5, peakEnd: 15 },
  };

  const window = maturityWindows[region || ''] || { peakStart: 5, peakEnd: 15 };

  if (wineAge < window.peakStart - 2) return 'unknown';
  if (wineAge >= window.peakStart - 2 && wineAge < window.peakStart) return 'approaching';
  if (wineAge >= window.peakStart && wineAge <= window.peakEnd) return 'peak';
  if (wineAge > window.peakEnd) return 'past';

  return 'unknown';
};

export const executeGetCellarInsights = async (): Promise<ToolExecutionResult> => {
  try {
    const [holdingsResponse, allocationsResponse] = await Promise.all([
      fetchCurrentHoldingVT2(),
      fetchCurrentAllocations(),
    ]);

    if (!holdingsResponse.data || holdingsResponse.data.length === 0) {
      return {
        success: true,
        data: {
          message: 'No wines found in cellar to analyze',
          insights: null,
        },
      };
    }

    const products = holdingsResponse.data;
    const totalValue = products.reduce((sum, p) => sum + (p.totalValue || 0), 0);

    // Maturity analysis
    const winesAtPeak: typeof products = [];
    const winesApproachingPeak: typeof products = [];
    const winesPastPeak: typeof products = [];

    // Performance analysis
    const strongPerformers: typeof products = [];
    const winesWithActiveBids: typeof products = [];
    const winesWithBidsAboveCost: typeof products = [];

    products.forEach((product) => {
      const maturity = getMaturityStatus(product.vintage, product.cultWinesAllocationRegion);

      if (maturity === 'approaching') winesApproachingPeak.push(product);
      if (maturity === 'peak') winesAtPeak.push(product);
      if (maturity === 'past') winesPastPeak.push(product);

      // Strong performer: >30% P&L and held > 2 years
      if (product.totalCost && product.totalCost > 0 && product.dealDate) {
        const pnlPct = ((product.profitAndLoss || 0) / product.totalCost) * 100;
        const dealDate = new Date(product.dealDate);
        const yearsHeld = (Date.now() - dealDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);

        if (pnlPct > 30 && yearsHeld >= 2) {
          strongPerformers.push(product);
        }
      }

      // Active bids
      const bidPrice = product.highestBid?.priceGbp ?? product.highestBidPrice ?? null;
      if (bidPrice !== null) {
        winesWithActiveBids.push(product);

        const costPerUnit = product.costPerUnit || (product.totalCost || 0) / product.qty;
        if (bidPrice > costPerUnit) {
          winesWithBidsAboveCost.push(product);
        }
      }
    });

    // Concentration warnings from allocations
    const concentrationWarnings: CellarInsightData['concentrationWarnings'] = [];

    if (allocationsResponse.portfolioCurrentAllocation) {
      allocationsResponse.portfolioCurrentAllocation.forEach((alloc) => {
        const percentage = alloc.currentAllocation * 100;
        if (percentage >= 50) {
          concentrationWarnings.push({
            type: 'region',
            subject: alloc.regionName,
            percentage: Math.round(percentage * 10) / 10,
          });
        }
      });
    }

    // Single wine concentration
    products.forEach((product) => {
      if (totalValue > 0) {
        const percentage = ((product.totalValue || 0) / totalValue) * 100;
        if (percentage >= 20) {
          concentrationWarnings.push({
            type: 'single-wine',
            subject: `${product.wineName}${product.vintage ? ` ${product.vintage}` : ''}`,
            percentage: Math.round(percentage * 10) / 10,
          });
        }
      }
    });

    // Format insights for LLM
    const formatWineList = (wines: typeof products, limit = 5) =>
      wines.slice(0, limit).map((w) => ({
        name: `${w.wineName}${w.vintage ? ` ${w.vintage}` : ''}`,
        value: formatCurrency(w.totalValue || 0),
        profitAndLoss:
          w.totalCost && w.totalCost > 0
            ? `${formatCurrency(w.profitAndLoss || 0)} (${formatPercentage(
                ((w.profitAndLoss || 0) / w.totalCost) * 100,
              )})`
            : formatCurrency(w.profitAndLoss || 0),
      }));

    return {
      success: true,
      data: {
        summary: {
          winesAtPeak: winesAtPeak.length,
          winesApproachingPeak: winesApproachingPeak.length,
          winesPastPeak: winesPastPeak.length,
          strongPerformers: strongPerformers.length,
          winesWithActiveBids: winesWithActiveBids.length,
          winesWithBidsAboveCost: winesWithBidsAboveCost.length,
          concentrationWarningsCount: concentrationWarnings.length,
        },
        maturityInsights: {
          atPeak: {
            count: winesAtPeak.length,
            message:
              winesAtPeak.length > 0
                ? `${winesAtPeak.length} wine(s) are currently in their optimal drinking window`
                : null,
            wines: formatWineList(winesAtPeak),
          },
          approachingPeak: {
            count: winesApproachingPeak.length,
            message:
              winesApproachingPeak.length > 0
                ? `${winesApproachingPeak.length} wine(s) are approaching their peak maturity`
                : null,
            wines: formatWineList(winesApproachingPeak),
          },
          pastPeak: {
            count: winesPastPeak.length,
            message:
              winesPastPeak.length > 0
                ? `${winesPastPeak.length} wine(s) are past their optimal drinking window - consider selling`
                : null,
            wines: formatWineList(winesPastPeak),
          },
        },
        marketInsights: {
          activeBids: {
            count: winesWithActiveBids.length,
            message:
              winesWithActiveBids.length > 0
                ? `${winesWithActiveBids.length} wine(s) have active buyer interest`
                : null,
            wines: formatWineList(winesWithActiveBids),
          },
          bidsAboveCost: {
            count: winesWithBidsAboveCost.length,
            message:
              winesWithBidsAboveCost.length > 0
                ? `${winesWithBidsAboveCost.length} wine(s) have bids above your purchase cost - selling opportunity`
                : null,
            wines: formatWineList(winesWithBidsAboveCost),
          },
        },
        performanceInsights: {
          strongPerformers: {
            count: strongPerformers.length,
            message:
              strongPerformers.length > 0
                ? `${strongPerformers.length} wine(s) showing strong long-term performance (>30% gain after 2+ years)`
                : null,
            wines: formatWineList(strongPerformers),
          },
        },
        concentrationWarnings:
          concentrationWarnings.length > 0
            ? concentrationWarnings.map((w) => ({
                type: w.type,
                message: `High concentration: ${w.subject} represents ${w.percentage}% of your portfolio`,
                subject: w.subject,
                percentage: `${w.percentage}%`,
              }))
            : [],
        recommendations: generateRecommendations({
          winesAtPeak: winesAtPeak.length,
          winesApproachingPeak: winesApproachingPeak.length,
          winesPastPeak: winesPastPeak.length,
          winesWithBidsAboveCost: winesWithBidsAboveCost.length,
          concentrationWarnings: concentrationWarnings.length,
        }),
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to fetch cellar insights: ${(error as Error).message}`,
    };
  }
};

const generateRecommendations = (counts: {
  winesAtPeak: number;
  winesApproachingPeak: number;
  winesPastPeak: number;
  winesWithBidsAboveCost: number;
  concentrationWarnings: number;
}): string[] => {
  const recommendations: string[] = [];

  if (counts.winesPastPeak > 0) {
    recommendations.push(`Consider reviewing ${counts.winesPastPeak} wine(s) past their peak for potential sale`);
  }

  if (counts.winesWithBidsAboveCost > 0) {
    recommendations.push(
      `Review ${counts.winesWithBidsAboveCost} wine(s) with bids above your cost - potential profit-taking opportunity`,
    );
  }

  if (counts.concentrationWarnings > 0) {
    recommendations.push('Your portfolio has concentration risks - consider diversifying across regions or wines');
  }

  if (counts.winesAtPeak > 3) {
    recommendations.push(`You have ${counts.winesAtPeak} wines at peak maturity - optimal time to enjoy or sell`);
  }

  return recommendations;
};
